import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useResources } from '../../hooks/useResources';
import { Resource } from '../../types/EducationHub';
import s from '../../styles/AdminResources.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMPTY_FORM = { title: '', description: '', category: '', tags: '' };

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminResources: React.FC = () => {
  const {
    fetchAdminResources, getResourceCategories,
    uploadResourceFile, createResource, updateResource, deleteResource,
  } = useResources();

  const [resources, setResources] = useState<Resource[]>([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState<File | null>(null);
  const [existingFileInfo, setExistingFileInfo] = useState<{ fileType?: string; fileSize?: number } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    setLoadingResources(true);
    try {
      const list = await fetchAdminResources();
      setResources(list);
    } finally {
      setLoadingResources(false);
    }
  }, [fetchAdminResources]);

  useEffect(() => {
    reload();
    getResourceCategories().then(setCategories);
  }, [reload, getResourceCategories]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleEdit = (resource: Resource) => {
    setEditingId(resource._id);
    setForm({
      title: resource.doc_name ?? '',
      description: resource.description ?? '',
      category: resource.category ?? '',
      tags: (resource.tags ?? []).join(', '),
    });
    setExistingFileInfo({ fileType: resource.fileType, fileSize: resource.fileSize });
    setFile(null);
    setFormError('');
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFile(null);
    setExistingFileInfo(null);
    setFormError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.category.trim()) {
      setFormError('Title, description, and category are required.');
      return;
    }
    if (editingId === null && !file) {
      setFormError('A file is required to create a resource.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      let uploaded: { fileUrl: string; s3Key: string; fileType: string; fileSize: number } | null = null;
      if (file) {
        uploaded = await uploadResourceFile(file);
      }

      const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category.trim(),
        tags,
        ...(uploaded ? {
          fileUrl: uploaded.fileUrl,
          s3Key: uploaded.s3Key,
          fileType: uploaded.fileType,
          fileSize: uploaded.fileSize,
        } : {}),
      };

      if (editingId !== null) {
        await updateResource(editingId, payload);
      } else {
        await createResource(payload as any);
      }
      handleCancel();
      await reload();
      getResourceCategories().then(setCategories);
    } catch {
      setFormError('Failed to save resource. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this resource? The uploaded file will also be removed.')) return;
    await deleteResource(id);
    await reload();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={s.page}>
      <h2 className={s.pageTitle}>📚 Resources Management</h2>

      {/* ── Form ───────────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>{editingId !== null ? 'Edit Resource' : 'Add Resource'}</h3>
        <div className={s.formGrid}>
          <label className={s.formLabel}>
            Title *
            <input className={s.input} placeholder="Resource title" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            Category *
            <input className={s.input} placeholder="e.g. Templates" value={form.category}
              list="resource-categories"
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
            <datalist id="resource-categories">
              {categories.map(c => <option key={c} value={c} />)}
            </datalist>
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Tags (comma-separated)
            <input className={s.input} placeholder="compliance, forms" value={form.tags}
              onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Description *
            <textarea className={s.textarea} rows={3} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            {editingId !== null ? 'Replace File (optional)' : 'File *'}
            <input ref={fileInputRef} type="file" className={s.input}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              onChange={e => setFile(e.target.files?.[0] ?? null)} />
            {editingId !== null && existingFileInfo && !file && (
              <span className={s.hint}>Current file: {existingFileInfo.fileType || 'unknown type'}, {formatFileSize(existingFileInfo.fileSize)}. Leave blank to keep it.</span>
            )}
          </label>
        </div>
        {formError && <p className={s.error}>{formError}</p>}
        <div className={s.formActions}>
          <button className={s.btnSave} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingId !== null ? 'Update' : 'Add Resource'}
          </button>
          {editingId !== null && (
            <button className={s.btnCancel} onClick={handleCancel}>Cancel</button>
          )}
        </div>
      </section>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>All Resources ({resources.length})</h3>
        {loadingResources ? <p className={s.loading}>Loading…</p> : (
          <div className={s.tableWrapper}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Tags</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {resources.map(resource => (
                  <tr key={resource._id}>
                    <td>{resource.doc_name}</td>
                    <td>{resource.category ?? '—'}</td>
                    <td>{resource.fileType ?? '—'}</td>
                    <td>{formatFileSize(resource.fileSize)}</td>
                    <td className={s.truncate}>{(resource.tags ?? []).join(', ') || '—'}</td>
                    <td className={s.actions}>
                      <button className={s.btnEdit} onClick={() => handleEdit(resource)}>Edit</button>
                      <button className={s.btnDelete} onClick={() => handleDelete(resource._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {resources.length === 0 && (
                  <tr><td colSpan={6} className={s.empty}>No resources yet. Add one above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminResources;
